import { Box, Flex, Grid, GridItem, Text, useTheme } from "@chakra-ui/react";
import { pickTextColorBasedOnBgColor } from "@picovico/utils";
import { get } from "lodash";
import { useRef } from "react";
import { SeatLayoutProps } from "../../@types/SeatLayout";
import { Screen } from "../../components/Screen";
import { seatColorEnum } from "../../constant";
import PanZoomPinchWrapper from "../PanZoomPinch/PanZoomPinchWrapper";

export function SeatLayoutRendererWithIconBg({
  seatLayouts,
  screenPosition,
  audiLayout,
  onSeatSelect,
  seatWrapperBoxProps,
  seatWrapperFlexProps,
  selectedSeats,
  disableSelectingSeats = false,
  screenSideBoxProps,
  screenTopScreenSideBoxPropsSx,
  screenBottomScreenSideBoxPropsSx,
  screenSideType = "BOX",
  panZoomPinchProps,
  showPanZoomPinchLayout = true,
  seatBoxWrapperProps,
  seatLabelTextProps,
  gridItemsProps,
}: SeatLayoutProps) {
  const theme = useTheme();
  const seatsRendererRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  if (!seatLayouts || seatLayouts?.length < 1) return null;
  console.log(disableSelectingSeats);
  return (
    <Flex
      justifyContent={
        screenPosition === "Bottom" ? "space-between" : "flex-end"
      }
      alignItems="center"
      direction={screenPosition === "Bottom" ? "column" : "column-reverse"}
      width="100vw"
      height="100%"
      px={{ base: "0.5rem", sm: "1rem" }}
      gap={4}
      {...seatWrapperFlexProps}
    >
      {showPanZoomPinchLayout ? (
        <PanZoomPinchWrapper
          seatLayoutRef={seatsRendererRef}
          isDraggingRef={isDraggingRef}
          showZoomControls={false}
          initialScale={0.6}
          centerOnInit={false}
          limitToBounds={true}
          {...panZoomPinchProps}
        >
          <Flex
            ref={seatsRendererRef}
            width={"100%"}
            justifyContent={"center"}
            overflow={"auto"}
          >
            <Box
              my={"1rem"}
              {...seatWrapperBoxProps}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: audiLayout === "row" ? "column" : "row",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                maxHeight: "100%",
                width: "fit-content",
                maxWidth: "100%",
                // overflow: "auto",
                ...(seatWrapperBoxProps?.sx || {}),
              }}
            >
              {seatLayouts?.map((seatLayout, index) => {
                const isEverySeatInActive = !seatLayout?.seats?.every(
                  (seat) => !seat.isActive,
                );
                return (
                  <Grid
                    key={`seat_grid_${index}`}
                    templateRows={
                      audiLayout === "col"
                        ? `repeat(${seatLayout?.seats?.length + 1},1fr)`
                        : ""
                    }
                    templateColumns={
                      audiLayout === "row"
                        ? `repeat(${seatLayout?.seats?.length + 1},1fr)`
                        : ""
                    }
                    gap={"4px"}
                    mb={isEverySeatInActive ? "0.5rem" : "0rem"}
                    width={"100%"}
                    justifyItems={"center"}
                  >
                    <GridItem
                      display={"flex"}
                      alignItems={"center"}
                      {...gridItemsProps}
                    >
                      <Box
                        bg={seatColorEnum["ROW LABEL"]}
                        height="25px"
                        width="25px"
                        borderRadius={"4px"}
                        opacity={isEverySeatInActive ? 1 : 0}
                      >
                        <Text
                          sx={{
                            color: pickTextColorBasedOnBgColor(
                              get(theme.colors, seatColorEnum["ROW LABEL"]) ||
                                "white",
                              theme?.colors?.white || "#fff",
                              theme?.colors?.surface[700] || "#111",
                            ),
                          }}
                          textAlign={"center"}
                        >
                          {seatLayout.row}
                        </Text>
                      </Box>
                    </GridItem>
                    {seatLayout.seats?.map((seat, seatIndex) => {
                      const seatColor: string = selectedSeats?.includes(
                        seat.seatIdx,
                      )
                        ? seatColorEnum.SELECTED
                        : seat.seat_status === "sold"
                          ? seatColorEnum.SOLD
                          : seat.seat_status === "booked"
                            ? seatColorEnum.BOOKED
                            : seat.seat_status === "hold"
                              ? seatColorEnum.BOOKED
                              : seatColorEnum.AVAILABLE;

                      return (
                        <GridItem
                          // maxW="2rem"
                          key={`seat_${seatIndex}`}
                          bg={seat.isActive ? seatColor : "transparent"}
                          borderRadius={4}
                          p={"4px"}
                          {...gridItemsProps}
                        >
                          <Box
                            position="relative"
                            cursor={
                              !seat.canSelect ||
                              !seat.isActive ||
                              disableSelectingSeats
                                ? "not-allowed"
                                : "pointer"
                            }
                            opacity={
                              !seat.canSelect || disableSelectingSeats ? 0.7 : 1
                            }
                            onClick={() => {
                              if (
                                !seat.canSelect ||
                                !seat.isActive ||
                                disableSelectingSeats ||
                                isDraggingRef.current
                              )
                                return;
                              onSeatSelect(seat);
                            }}
                            height={"25px"}
                            width={"25px"}
                            _hover={{
                              opacity: 0.8,
                              transition: "all 0.2s ease-in-out",
                            }}
                            {...seatBoxWrapperProps}
                            sx={{
                              // backgroundColor: seatColor,
                              borderRadius: 4,
                              ...seatBoxWrapperProps?.sx,
                            }}
                          >
                            <Box
                              as="div"
                              textAlign={"center"}
                              sx={{
                                backgroundImage: `url(${seat.icon})`,
                                backgroundSize: "contain",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                width: "100%",
                                height: "100%",
                                // borderRadius: "4px",
                                transition: "all 0.2s ease-in-out",
                                opacity: seat.isActive ? 1 : 0,
                                padding: "2px",
                                filter: !seat.canSelect
                                  ? "grayscale(1)"
                                  : "none",
                              }}
                            />
                          </Box>
                          {seat.isActive && seat.seatLabel && (
                            <Text
                              color={"white"}
                              fontSize={"10px"}
                              textAlign={"center"}
                              bg={"black"}
                              borderRadius={6}
                              {...seatLabelTextProps}
                            >
                              {seat.seatLabel}
                            </Text>
                          )}
                        </GridItem>
                      );
                    })}
                  </Grid>
                );
              })}
            </Box>
          </Flex>
        </PanZoomPinchWrapper>
      ) : (
        <Flex width={"100%"} justifyContent={"center"} overflow={"auto"}>
          <Box
            my={"1rem"}
            {...seatWrapperBoxProps}
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: audiLayout === "row" ? "column" : "row",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              maxHeight: "100%",
              width: "fit-content",
              maxWidth: "100%",
              // overflow: "auto",
              ...(seatWrapperBoxProps?.sx || {}),
            }}
          >
            {seatLayouts?.map((seatLayout, index) => {
              const isEverySeatInActive = !seatLayout?.seats?.every(
                (seat) => !seat.isActive,
              );
              return (
                <Grid
                  key={`seat_grid_${index}`}
                  templateRows={
                    audiLayout === "col"
                      ? `repeat(${seatLayout?.seats?.length + 1},1fr)`
                      : ""
                  }
                  templateColumns={
                    audiLayout === "row"
                      ? `repeat(${seatLayout?.seats?.length + 1},1fr)`
                      : ""
                  }
                  gap={"4px"}
                  mb={"1rem"}
                  width={"100%"}
                  justifyItems={"center"}
                >
                  <GridItem
                    display={"flex"}
                    alignItems={"center"}
                    {...gridItemsProps}
                  >
                    <Box
                      bg={seatColorEnum["ROW LABEL"]}
                      height="25px"
                      width="25px"
                      borderRadius={"4px"}
                      opacity={isEverySeatInActive ? 1 : 0}
                    >
                      <Text
                        sx={{
                          color: pickTextColorBasedOnBgColor(
                            get(theme.colors, seatColorEnum["ROW LABEL"]) ||
                              "white",
                            theme?.colors?.white || "#fff",
                            theme?.colors?.surface[700] || "#111",
                          ),
                        }}
                        textAlign={"center"}
                      >
                        {seatLayout.row}
                      </Text>
                    </Box>
                  </GridItem>
                  {seatLayout.seats?.map((seat, seatIndex) => {
                    const seatColor: string = selectedSeats?.includes(
                      seat.seatIdx,
                    )
                      ? seatColorEnum.SELECTED
                      : seat.seat_status === "sold"
                        ? seatColorEnum.SOLD
                        : seat.seat_status === "booked"
                          ? seatColorEnum.BOOKED
                          : seat.seat_status === "hold"
                            ? seatColorEnum.BOOKED
                            : seatColorEnum.AVAILABLE;

                    return (
                      <GridItem
                        // maxW="2rem"
                        key={`seat_${seatIndex}`}
                        bg={seatColor}
                        borderRadius={4}
                        border={1}
                        px={"2px"}
                        {...gridItemsProps}
                      >
                        <Box
                          position="relative"
                          cursor={
                            !seat.canSelect ||
                            !seat.isActive ||
                            disableSelectingSeats
                              ? "not-allowed"
                              : "pointer"
                          }
                          opacity={
                            !seat.canSelect || disableSelectingSeats ? 0.7 : 1
                          }
                          onClick={() => {
                            if (
                              !seat.canSelect ||
                              !seat.isActive ||
                              disableSelectingSeats
                            )
                              return;
                            onSeatSelect(seat);
                          }}
                          height={"25px"}
                          width={"25px"}
                          _hover={{
                            opacity: 0.8,
                            transition: "all 0.2s ease-in-out",
                          }}
                          {...seatBoxWrapperProps}
                          sx={{
                            backgroundColor: seatColor,
                            borderRadius: 4,
                            ...seatBoxWrapperProps?.sx,
                          }}
                        >
                          <Box
                            as="div"
                            textAlign={"center"}
                            sx={{
                              backgroundImage: `url(${seat.icon})`,
                              backgroundSize: "contain",
                              backgroundPosition: "center",
                              backgroundRepeat: "no-repeat",
                              backgroundColor: seatColor,
                              width: "100%",
                              height: "100%",
                              borderRadius: "4px",
                              transition: "all 0.2s ease-in-out",
                              opacity: seat.isActive ? 1 : 0,
                              padding: "2px",
                            }}
                          />
                        </Box>
                        {seat.isActive && seat.seatLabel && (
                          <Text
                            color={"white"}
                            fontSize={"10px"}
                            textAlign={"center"}
                            bg={"black"}
                            borderRadius={6}
                            {...seatLabelTextProps}
                          >
                            {seat.seatLabel}
                          </Text>
                        )}
                      </GridItem>
                    );
                  })}
                </Grid>
              );
            })}
          </Box>
        </Flex>
      )}

      {screenSideType === "CURVE" ? (
        <Screen screenPosition={screenPosition} {...screenSideBoxProps} />
      ) : (
        <Box
          p={{
            base: "0.25rem",
            md: "0.5rem",
          }}
          my={{
            base: "0.5rem",
            md: "1rem",
          }}
          width={{
            base: "80vw",
            md: "80vw",
            sm: "full",
          }}
          background="surface.500"
          {...screenSideBoxProps}
          sx={{
            position: "relative",
            textAlign: "center",
            color: "white",
            fontWeight: 600,
            borderBottomRightRadius:
              screenPosition === "Bottom" ? "3xl" : "none",
            borderBottomLeftRadius:
              screenPosition === "Bottom" ? "3xl" : "none",
            borderTopRightRadius: screenPosition === "Top" ? "none" : "3xl",
            borderTopLeftRadius: screenPosition === "Top" ? "none" : "3xl",
            mb: screenPosition === "Top" ? "auto" : 0,
            mt: screenPosition === "Bottom" ? "auto" : 0,
            ...screenSideBoxProps?.sx,
            ...(screenPosition === "Top"
              ? screenTopScreenSideBoxPropsSx
              : screenBottomScreenSideBoxPropsSx),
          }}
        >
          <Text>Screen this side</Text>
        </Box>
      )}
    </Flex>
  );
}
